import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  highlight?: number;
  label?: string;
}
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-5709">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-5710">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-5711">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-5712">
            <span className="text-2xl" data-id="element-5713">🔤</span>
            <div className="text-left" data-id="element-5714">
              <div className="text-sm font-bold text-green-900" data-id="element-5715">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-5716">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-5717">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-5718">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-5719">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-5720">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-5721">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-5722">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = highlight === index;
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-5723">

                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-5724">

                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-5725">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                            `} data-id="element-5726">

                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-5727">
                      <span className="text-xs font-bold" data-id="element-5728">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-5729">

                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-5730">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-5731">
            <div className="flex items-start gap-3" data-id="element-5732">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-5733">
                <span className="text-blue-600 text-lg" data-id="element-5734">👀</span>
              </div>
              <div data-id="element-5735">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-5736">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5737">
                  <p data-id="element-5738">
                    Het woord <strong data-id="element-5739">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-5740">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-5741">
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-5742">

                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-5743">
                    <p className="flex items-center gap-2" data-id="element-5744">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-5745"></span>
                      <span data-id="element-5746">
                        <strong data-id="element-5747">Groen (○):</strong> Open lettergreep = lange
                        klank (één medeklinker na de klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-5748">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-5749"></span>
                      <span data-id="element-5750">
                        <strong data-id="element-5751">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (twee medeklinkers na de klinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-5752">
            <div className="flex items-start gap-3" data-id="element-5753">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-5754">
                <span className="text-emerald-600 text-lg" data-id="element-5755">📖</span>
              </div>
              <div data-id="element-5756">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-5757">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-5758">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-5759">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-5760">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-5761">
                      Eindigt op een <strong data-id="element-5762">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-5763">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-5764">
                      Voorbeeld: <strong data-id="element-5765">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-5766">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-5767">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-5768">
                      Eindigt op een <strong data-id="element-5769">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-5770">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-5771">
                      Voorbeeld: <strong data-id="element-5772">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-5773">
            <div className="flex items-start gap-3" data-id="element-5774">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-5775">
                <span className="text-indigo-600 text-lg" data-id="element-5776">✍️</span>
              </div>
              <div data-id="element-5777">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-5778">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-5779">
                  <p data-id="element-5780">
                    <strong data-id="element-5781">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-5782">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-5783">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-5784">
                    <strong data-id="element-5785">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-5786">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-5787">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-5788">
            <div className="flex items-start gap-3" data-id="element-5789">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-5790">
                <span className="text-amber-600 text-lg" data-id="element-5791">💡</span>
              </div>
              <div data-id="element-5792">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-5793">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5794">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-5795">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-5796">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-5797">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-5798">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-5799">open</strong> en schrijf je{' '}
                  <strong data-id="element-5800">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}